import { createWithEqualityFn } from 'zustand/traditional'
import { createJSONStorage, persist } from 'zustand/middleware'

import { normalizeGameResultReceipts } from '@runtime/core/user/gameResultReceipts'
import { normalizeGameResultsOutbox } from '@runtime/core/user/gameResultsOutbox'
import type { AppStoreState } from '@runtime/core/store/appStoreTypes'
import {
  STORE_KEY,
  normalizeLoadedUser,
} from '@runtime/core/store/appStorePersist'
import { createGamesSlice } from '@runtime/core/store/slices/gamesSlice'
import { createPrefsSlice } from '@runtime/core/store/slices/prefsSlice'
import { createUiSlice } from '@runtime/core/store/slices/uiSlice'
import { createUserSlice } from '@runtime/core/store/slices/userSlice'

export type {
  AppStoreState,
  AvatarColor,
  GamePlayerSummary,
  GameSummary,
  StrategyHelpMode,
} from '@runtime/core/store/appStoreTypes'

export const useAppStore = createWithEqualityFn<AppStoreState>()(
  persist(
    (...args) => ({
      ...createPrefsSlice(...args),
      ...createUiSlice(...args),
      ...createUserSlice(...args),
      ...createGamesSlice(...args),
    }),
    {
      name: STORE_KEY,
      version: 5,
      storage: createJSONStorage(() => localStorage),

      // Persist only durable settings/data.
      partialize: (s) => ({
        themeId: s.themeId,
        themeTokens: s.themeTokens,
        locale: s.locale,
        strategyHelpMode: s.strategyHelpMode,
        onlineNick: s.onlineNick,
        onlineAvatarColor: s.onlineAvatarColor,
        onlineSoundEnabled: s.onlineSoundEnabled,
        arcadeIntroSeen: s.arcadeIntroSeen,
        arcadeSfxEnabled: s.arcadeSfxEnabled,
        arcadeVibrationEnabled: s.arcadeVibrationEnabled,
        arcadeEnergyStartLevel: s.arcadeEnergyStartLevel,
        arcadeFusionObstacles: s.arcadeFusionObstacles,
        arcadeFusionTimeMin: s.arcadeFusionTimeMin,
        posterPrivateUnlockedAll: s.posterPrivateUnlockedAll,
        posterPrivateUnlockedIds: s.posterPrivateUnlockedIds,
        authToken: s.authToken,
        user: s.user,
        gameResultsOutbox: s.gameResultsOutbox,
        gameResultReceipts: s.gameResultReceipts,
        gameResultsVersion: s.gameResultsVersion,
      }),

      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<AppStoreState>
        return {
          ...current,
          ...saved,
          gameResultsVersion: 1,
          gameResultReceipts: normalizeGameResultReceipts(
            saved.gameResultReceipts,
          ),
          gameResultsOutbox: normalizeGameResultsOutbox(
            saved.gameResultsOutbox,
            saved.authToken ? normalizeLoadedUser(saved.user) : null,
            saved.gameResultsVersion !== 1,
          ),
        }
      },
      onRehydrateStorage: () => (state) => {
        if (state && state.user) {
          const norm = normalizeLoadedUser(state.user)
          if (norm) state.user = norm
        }
      },
    },
  ),
)

// Convenience selectors (avoid repeating deep paths across the app)
export const selectPosterOpen = (s: AppStoreState) => s.ui.posterOpen
export const selectLoginOpen = (s: AppStoreState) => s.ui.loginOpen
